import { redactPathIds } from '#utils/redactPathIds.ts';

/**
 * Attribution capture is deliberately NOT gated on cookie consent, unlike every vendor script in
 * `apps/www`. Product decision (2026-09-21, John): the cookie is first-party, is never shared with
 * a vendor, and holds no data beyond the marketing parameters the visitor's own URL already
 * carried. Flip this to `true` and gate the `Attribution.capture()` call sites behind CookieHub's
 * "analytics" category to reverse it — it is one switch on purpose.
 */
export const ATTRIBUTION_REQUIRES_CONSENT = false;

export interface AttributionTouch {
  ts: string;
  /** Path only, with uuid segments redacted. The query string is NEVER stored — see `buildTouch`. */
  landingPath: string;
  /** Bare hostname of an external referrer; `null` for direct visits and our own domains. */
  referrerHost: string | null;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  clickIds?: Record<string, string>;
}

/** The last www CTA clicked. Same-domain hops leave no referrer, so the click is recorded itself. */
export interface CtaTouch {
  ts: string;
  /** `start_free` or `book_demo`. */
  cta: string;
  /** The link's `data-cta` in apps/www, e.g. `hero`. */
  placement: string;
  path: string;
}

/**
 * Three slots, because two cannot express "first non-direct touch": with only first/last, a
 * direct → ChatGPT → LinkedIn journey overwrites the ChatGPT touch and attributes to LinkedIn.
 * `firstSignalTouch` is written once and never overwritten, so the acquisition channel is stable
 * for the life of the cookie; `lastSignalTouch` moves, and is what campaign-response questions read.
 */
export interface AttributionSnapshot {
  firstTouch: AttributionTouch;
  firstSignalTouch: AttributionTouch | null;
  lastSignalTouch: AttributionTouch | null;
  lastCta?: CtaTouch;
}

interface BuildTouchParams {
  url: URL;
  referrer: string;
  now: Date;
}

interface SerializeCookieParams {
  snapshot: AttributionSnapshot;
  hostname: string;
  secure: boolean;
}

export const ATTRIBUTION_COOKIE_NAME = 'blockit_attribution';

const COOKIE_MAX_AGE_DAYS = 90;
const SECONDS_PER_DAY = 86_400;
const COOKIE_MAX_AGE_SECONDS = COOKIE_MAX_AGE_DAYS * SECONDS_PER_DAY;
const OWN_DOMAIN = 'blockit.com';
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0']);
/** Kept in step with the server's booking-page rule in `acquisitionAttributionService`. */
const BOOKING_PAGE_PATH_PREFIX = '/link/';

/**
 * Browsers silently drop a cookie over ~4096 bytes, so an over-long value loses attribution with
 * no error — and it would lose it for exactly the visits carrying the most parameters. The field
 * caps keep the common case near 800 bytes; `serializeCookie`'s ladder enforces the rest.
 */
const MAX_PATH_LENGTH = 192;
const MAX_HOST_LENGTH = 96;
const MAX_VALUE_LENGTH = 96;
const MAX_CLICK_IDS = 4;
const MAX_COOKIE_BYTES = 3500;

/**
 * The allowlist IS the privacy boundary. Only these parameters are read out of the URL; the rest of
 * the query string is discarded rather than redacted, so a secret nobody thought of (the reschedule
 * token `t`, a password-reset code) can never reach the cookie, the request body or the database.
 */
const UTM_PARAMS = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmContent: 'utm_content',
  utmTerm: 'utm_term',
} as const;

// fbclid is captured as a signal even though Facebook appends it to organic clicks too; the server
// decides whether a click id implies paid. `oppref` is ChatGPT Ads' click reference — the only thing
// separating an ad click from an organic ChatGPT citation, which carries the same utm_source.
const CLICK_ID_PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  'msclkid',
  'fbclid',
  'li_fat_id',
  'ttclid',
  'twclid',
  'oppref',
];

const truncate = (value: string, maxLength: number) => (value.length > maxLength ? value.slice(0, maxLength) : value);

const isOwnHostname = (hostname: string) =>
  hostname === OWN_DOMAIN || hostname.endsWith(`.${OWN_DOMAIN}`) || LOCAL_HOSTNAMES.has(hostname);

const referrerHostname = (referrer: string): string | null => {
  if (!referrer) {
    return null;
  }
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return null;
  }
};

/**
 * Only the host is kept. The derivation never looks past it, `www.blockit.com` already sends
 * `Referrer-Policy: strict-origin-when-cross-origin` (so a cross-origin referrer has no path
 * anyway), and a full referrer URL can carry someone else's search query or internal-tool path.
 */
const externalReferrerHost = (referrer: string): string | null => {
  const hostname = referrerHostname(referrer);
  return hostname === null || isOwnHostname(hostname) ? null : truncate(hostname, MAX_HOST_LENGTH);
};

/**
 * Customers share booking links untagged, so arriving on one is the signal itself. A hop from our
 * own pages (the book-demo redirect) is not: that visitor came from Blockit, not from a customer.
 */
const isBookingPageEntry = ({ touch, referrer }: { touch: AttributionTouch; referrer: string }) => {
  const hostname = referrerHostname(referrer);
  return touch.landingPath.startsWith(BOOKING_PAGE_PATH_PREFIX) && !(hostname !== null && isOwnHostname(hostname));
};

const buildTouch = ({ url, referrer, now }: BuildTouchParams): AttributionTouch => {
  const touch: AttributionTouch = {
    ts: now.toISOString(),
    landingPath: truncate(redactPathIds(url.pathname), MAX_PATH_LENGTH),
    referrerHost: externalReferrerHost(referrer),
  };

  for (const [field, param] of Object.entries(UTM_PARAMS) as [keyof typeof UTM_PARAMS, string][]) {
    const value = url.searchParams.get(param)?.trim();
    if (value) {
      touch[field] = truncate(value, MAX_VALUE_LENGTH);
    }
  }

  const clickIds: Record<string, string> = {};
  for (const param of CLICK_ID_PARAMS) {
    if (Object.keys(clickIds).length >= MAX_CLICK_IDS) {
      break;
    }
    const value = url.searchParams.get(param)?.trim();
    if (value) {
      clickIds[param] = truncate(value, MAX_VALUE_LENGTH);
    }
  }
  if (Object.keys(clickIds).length > 0) {
    touch.clickIds = clickIds;
  }

  return touch;
};

const hasSignal = (touch: AttributionTouch) =>
  touch.referrerHost !== null ||
  touch.clickIds !== undefined ||
  Object.keys(UTM_PARAMS).some((field) => touch[field as keyof typeof UTM_PARAMS] !== undefined);

/** Returns the snapshot to persist, or `null` when this touch changes nothing worth writing. */
const merge = (
  existing: AttributionSnapshot | null,
  touch: AttributionTouch,
  { bookingPageEntry = false }: { bookingPageEntry?: boolean } = {},
): AttributionSnapshot | null => {
  const touchHasSignal = bookingPageEntry || hasSignal(touch);
  if (!existing) {
    return {
      firstTouch: touch,
      firstSignalTouch: touchHasSignal ? touch : null,
      lastSignalTouch: touchHasSignal ? touch : null,
    };
  }
  // An internal page-to-page navigation carries no signal and must not disturb what is stored.
  if (!touchHasSignal) {
    return null;
  }
  return {
    firstTouch: existing.firstTouch,
    firstSignalTouch: existing.firstSignalTouch ?? touch,
    lastSignalTouch: touch,
    lastCta: existing.lastCta,
  };
};

/** The touch the acquisition channel is derived from: the first that carried a signal, else the first ever. */
const primaryTouch = (snapshot: AttributionSnapshot): AttributionTouch =>
  snapshot.firstSignalTouch ?? snapshot.firstTouch;

const isTouch = (value: unknown): value is AttributionTouch =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as AttributionTouch).ts === 'string' &&
  typeof (value as AttributionTouch).landingPath === 'string';

const parseCookie = (cookieHeader: string): AttributionSnapshot | null => {
  const entry = cookieHeader
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${ATTRIBUTION_COOKIE_NAME}=`));
  if (!entry) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(entry.slice(ATTRIBUTION_COOKIE_NAME.length + 1)));
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const { firstTouch, firstSignalTouch, lastSignalTouch, lastCta } = parsed as Partial<AttributionSnapshot>;
    if (!isTouch(firstTouch)) {
      return null;
    }
    return {
      firstTouch,
      firstSignalTouch: isTouch(firstSignalTouch) ? firstSignalTouch : null,
      lastSignalTouch: isTouch(lastSignalTouch) ? lastSignalTouch : null,
      lastCta: typeof lastCta?.cta === 'string' ? lastCta : undefined,
    };
  } catch {
    return null;
  }
};

const withoutClickIds = (touch: AttributionTouch): AttributionTouch => {
  if (!touch.clickIds) {
    return touch;
  }
  const { clickIds: _dropped, ...rest } = touch;
  return rest;
};

/**
 * Degrades deterministically rather than letting the browser drop an oversized cookie on the floor.
 * The primary touch is the last thing to go, because it is the one the channel is derived from.
 */
const encodeWithinBudget = (snapshot: AttributionSnapshot): string | null => {
  const primary = primaryTouch(snapshot);
  const rungs: AttributionSnapshot[] = [
    snapshot,
    { ...snapshot, lastSignalTouch: null },
    {
      firstTouch: withoutClickIds(snapshot.firstTouch),
      firstSignalTouch: snapshot.firstSignalTouch && withoutClickIds(snapshot.firstSignalTouch),
      lastSignalTouch: null,
    },
    { firstTouch: primary, firstSignalTouch: hasSignal(primary) ? primary : null, lastSignalTouch: null },
  ];

  for (const rung of rungs) {
    const encoded = encodeURIComponent(JSON.stringify(rung));
    if (encoded.length <= MAX_COOKIE_BYTES) {
      return encoded;
    }
  }
  return null;
};

/** Returns the `document.cookie` assignment, or `null` when even the smallest form exceeds the budget. */
const serializeCookie = ({ snapshot, hostname, secure }: SerializeCookieParams): string | null => {
  const encoded = encodeWithinBudget(snapshot);
  if (!encoded) {
    return null;
  }
  const parts = [
    `${ATTRIBUTION_COOKIE_NAME}=${encoded}`,
    'path=/',
    `max-age=${COOKIE_MAX_AGE_SECONDS}`,
    'samesite=lax',
  ];
  // Scoped to the apex so www.blockit.com and app.blockit.com share one cookie. Left host-scoped
  // everywhere else, which keeps preview deployments and localhost isolated from each other.
  if (hostname === OWN_DOMAIN || hostname.endsWith(`.${OWN_DOMAIN}`)) {
    parts.push(`domain=.${OWN_DOMAIN}`);
  }
  if (secure) {
    parts.push('secure');
  }
  return parts.join('; ');
};

const read = (): AttributionSnapshot | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  return parseCookie(document.cookie);
};

const write = (snapshot: AttributionSnapshot): void => {
  const cookie = serializeCookie({
    snapshot,
    hostname: window.location.hostname,
    secure: window.location.protocol === 'https:',
  });
  if (cookie) {
    // biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API is unsupported in Safari and Firefox, and www + app must both read this cookie
    document.cookie = cookie;
  }
};

/**
 * Records the current page load as a touch. Safe to call on every page; writes only when something
 * changed. Safari's ITP caps a JS-written cookie at 7 days regardless of `max-age`, so the 90-day
 * window is the ceiling on Chrome/Firefox rather than a guarantee everywhere.
 *
 * Flipping `ATTRIBUTION_REQUIRES_CONSENT` makes this fail closed: capture stops everywhere until
 * each call site is updated to pass `hasConsent`, rather than silently continuing to write.
 */
const capture = ({ hasConsent }: { hasConsent?: boolean } = {}): void => {
  if (ATTRIBUTION_REQUIRES_CONSENT && hasConsent !== true) {
    return;
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  const touch = buildTouch({ url: new URL(window.location.href), referrer: document.referrer, now: new Date() });
  const next = merge(read(), touch, { bookingPageEntry: isBookingPageEntry({ touch, referrer: document.referrer }) });
  if (next) {
    write(next);
  }
};

/** Stamps a CTA click on the cookie `capture` wrote; a no-op without one. Gated like `capture`. */
const recordCta = ({ cta, placement, hasConsent }: { cta: string; placement: string; hasConsent?: boolean }): void => {
  const snapshot = ATTRIBUTION_REQUIRES_CONSENT && hasConsent !== true ? null : read();
  if (snapshot) {
    const path = truncate(redactPathIds(window.location.pathname), MAX_PATH_LENGTH);
    write({ ...snapshot, lastCta: { ts: new Date().toISOString(), cta, placement, path } });
  }
};

export const Attribution = {
  buildTouch,
  hasSignal,
  isBookingPageEntry,
  merge,
  primaryTouch,
  parseCookie,
  serializeCookie,
  read,
  capture,
  recordCta,
};
